import request from "graphql-request";
import { useRouter } from "next/router";
import { createContext, useContext, useEffect, useState } from "react";
import { baseUrl } from "services/shared/Environment";

export interface ArticlePageContextValue {
  authorName: string;
  categoriesListForAnalytics: string;
  articleTitle: string;
  articleId: string;
  articleSlug: string;
  referrerEmail: string;
  initialized: boolean;
}

function storyQuery(storySlug: string) {
  return `
      query {
        story(storySlug: "${storySlug}") {
            id
            title
            author {
                fullName
            }
            categories {
                name
            }
        }
      }
  `;
}

function articleReferrerEmailQuery(sharedStoryLinkId: string) {
  return `
    query {
      articleReferrerEmail(sharedStoryLinkId: "${sharedStoryLinkId}") 
    }
  `;
}

const defaultContextValues: ArticlePageContextValue = {
  authorName: "",
  categoriesListForAnalytics: "",
  articleTitle: "",
  articleId: "",
  articleSlug: "",
  referrerEmail: "",
  initialized: false
};

export const ArticlePageContext = createContext(defaultContextValues);

async function getArticleContextValue(
  slug: string,
  sharedStoryLinkId?: string
): Promise<ArticlePageContextValue> {
  try {
    const articleData = await request(
      `${baseUrl()}/api/graphql`,
      storyQuery(slug)
    );
    const authorName = articleData?.story?.author?.fullName ?? "";
    const articleId = articleData?.story?.id ?? "";
    const articleTitle = articleData?.story?.title ?? "";
    const articleCategories = articleData?.story?.categories ?? [];
    let categoriesListForAnalytics = "";
    for (let category of articleCategories) {
      if (categoriesListForAnalytics === "") {
        categoriesListForAnalytics = `${category?.name}`;
      } else {
        categoriesListForAnalytics = `, ${category?.name}`;
      }
    }

    let referrerEmail = "";
    if (sharedStoryLinkId) {
      const referrerEmailData = await request(
        `${baseUrl()}/api/graphql`,
        articleReferrerEmailQuery(sharedStoryLinkId)
      );
      referrerEmail = referrerEmailData?.articleReferrerEmail ?? "";
    }

    return {
      authorName,
      articleId,
      articleTitle,
      categoriesListForAnalytics,
      articleSlug: slug,
      referrerEmail,
      initialized: true
    };
  } catch (e) {
    return {
      ...defaultContextValues,
      initialized: true
    }
  }
}

export const ArticlePageContextProvider: React.FC = ({ children }) => {
  const [context, setContext] = useState<ArticlePageContextValue>(
    defaultContextValues
  );
  const router = useRouter();
  const slug = (router.query.slug as string) ?? "";
  const sharedStoryLinkId = router.query.s as string | undefined

  useEffect(() => {
    if (slug) {
      getArticleContextValue(slug, sharedStoryLinkId).then((contextValue) => {
        setContext(contextValue);
      });
    }
  }, [slug, sharedStoryLinkId]);

  return (
    <ArticlePageContext.Provider value={context}>
      {children}
    </ArticlePageContext.Provider>
  );
};

export function useArticlePageContext() {
  const context = useContext(ArticlePageContext);
  return context;
}
